'use client'

import * as React from 'react'
import { AlertTriangle, CheckCircle, Clock, Plus } from 'lucide-react'
import { addDoc, collection, getDocs, query, serverTimestamp, where } from 'firebase/firestore'
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage'
import { Badge } from 'components/ui/badge'
import { Button } from 'components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'components/ui/card'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from 'components/ui/dialog'
import { Input } from 'components/ui/input'
import { Label } from 'components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select'
import { Progress } from 'components/ui/progress'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table'
import { Textarea } from 'components/ui/textarea'
import { toast } from 'sonner'
import { db, storage } from '../../../../firebase'
import { useProject } from './project-context'
import { useUser } from 'components/context/user-context'

function toDateValue(value) {
  if (!value) return null
  if (typeof value?.toDate === 'function') return value.toDate()
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return null
  return parsed
}

function formatDate(value) {
  const parsed = toDateValue(value)
  if (!parsed) return '—'
  return parsed.toLocaleDateString('en-KE', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

function getIndicatorTarget(indicator) {
  if (Number.isFinite(indicator?.target)) return Number(indicator.target)
  const firstTarget = indicator?.targets?.[0]?.value
  return Number(firstTarget || 0)
}

export default function ProjectTrackingPage() {
  const { project } = useProject()
  const { canEdit } = useUser()
  const [deliverables, setDeliverables] = React.useState([])
  const [indicators, setIndicators] = React.useState([])
  const [risks, setRisks] = React.useState([])
  const [isLoading, setIsLoading] = React.useState(true)
  const [indicatorOpen, setIndicatorOpen] = React.useState(false)
  const [savingIndicator, setSavingIndicator] = React.useState(false)
  const [indicatorFile, setIndicatorFile] = React.useState(null)
  const [indicatorForm, setIndicatorForm] = React.useState({
    name: '',
    level: 'Output',
    baseline: '',
    target: '',
    current: '',
    dataSource: '',
  })

  React.useEffect(() => {
    let isMounted = true

    async function loadTrackingData() {
      try {
        const [deliverableSnap, indicatorSnap, riskSnap] = await Promise.all([
          getDocs(query(collection(db, 'deliverables'), where('projectId', '==', project.id))),
          getDocs(query(collection(db, 'indicators'), where('projectId', '==', project.id))),
          getDocs(query(collection(db, 'risks'), where('projectId', '==', project.id))),
        ])
        if (!isMounted) return
        setDeliverables(deliverableSnap.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() })))
        setIndicators(indicatorSnap.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() })))
        setRisks(riskSnap.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() })))
      } catch {
        if (!isMounted) return
        setDeliverables([])
        setIndicators([])
        setRisks([])
      } finally {
        if (isMounted) setIsLoading(false)
      }
    }

    loadTrackingData()

    return () => {
      isMounted = false
    }
  }, [project.id])

  const totalDeliverables = deliverables.length
  const completedDeliverables = deliverables.filter((d) => d.status === 'completed').length
  const completionRate = totalDeliverables > 0 ? (completedDeliverables / totalDeliverables) * 100 : 0

  const startDate = toDateValue(project.startDate) || toDateValue(deliverables[0]?.plannedStart)
  const endDate = toDateValue(project.endDate) || toDateValue(deliverables[0]?.plannedEnd)
  const totalDays =
    startDate && endDate
      ? Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24))
      : 0
  const elapsedDays = startDate
    ? Math.ceil((Date.now() - startDate.getTime()) / (1000 * 60 * 60 * 24))
    : 0
  const timeProgress = totalDays > 0 ? Math.min(100, Math.max(0, (elapsedDays / totalDays) * 100)) : 0

  const delta = completionRate - timeProgress
  const trackingStatus = totalDays === 0
    ? 'Not Scheduled'
    : delta < -15
      ? 'Delayed'
      : delta < -5
        ? 'At Risk'
        : 'On Track'

  const openRisks = risks.filter((risk) => String(risk.status || '').toLowerCase() === 'open').length
  const highRisks = risks.filter((risk) => Number(risk.riskScore || 0) >= 10).length

  const milestoneItems = deliverables
    .filter((item) => item.plannedEnd)
    .sort((a, b) => {
      const aDate = toDateValue(a.plannedEnd)?.getTime() || 0
      const bDate = toDateValue(b.plannedEnd)?.getTime() || 0
      return aDate - bDate
    })
    .slice(0, 6)

  const resetIndicatorForm = () => {
    setIndicatorForm({
      name: '',
      level: 'Output',
      baseline: '',
      target: '',
      current: '',
      dataSource: '',
    })
    setIndicatorFile(null)
  }

  const handleSaveIndicator = async () => {
    if (!indicatorForm.name.trim()) {
      toast.error('Please enter an indicator name.')
      return
    }
    if (savingIndicator) return
    setSavingIndicator(true)
    try {
      let evidence = null
      if (indicatorFile) {
        const safeName = indicatorFile.name.replace(/[^a-zA-Z0-9._-]/g, '_')
        const uniqueSuffix = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
        const path = `indicator-documents/${project.id}/${uniqueSuffix}-${safeName}`
        const storageRef = ref(storage, path)
        await uploadBytes(storageRef, indicatorFile, { contentType: indicatorFile.type })
        const url = await getDownloadURL(storageRef)
        evidence = {
          name: indicatorFile.name,
          url,
          path,
          size: indicatorFile.size,
          type: indicatorFile.type,
        }
      }

      const payload = {
        projectId: project.id,
        name: indicatorForm.name.trim(),
        level: indicatorForm.level,
        baseline: Number(indicatorForm.baseline || 0),
        target: Number(indicatorForm.target || 0),
        actual: Number(indicatorForm.current || 0),
        dataSource: indicatorForm.dataSource.trim(),
        evidence,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      }
      const docRef = await addDoc(collection(db, 'indicators'), payload)
      setIndicators((prev) => [{ id: docRef.id, ...payload }, ...prev])
      toast.success('Indicator added.')
      resetIndicatorForm()
      setIndicatorOpen(false)
    } catch (error) {
      toast.error('Failed to add indicator', {
        description: error?.message || 'Please try again.',
      })
    } finally {
      setSavingIndicator(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Deliverables</p>
            <p className="text-2xl font-bold text-foreground">{completedDeliverables}/{totalDeliverables}</p>
            <p className="text-xs text-muted-foreground">completed</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Timeline Progress</p>
            <p className="text-2xl font-bold text-foreground">{timeProgress.toFixed(0)}%</p>
            <Progress value={timeProgress} className="mt-2 h-1.5" />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Completion Rate</p>
            <p className="text-2xl font-bold text-foreground">{completionRate.toFixed(0)}%</p>
            <Progress value={completionRate} className="mt-2 h-1.5" />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Overall Status</p>
            <div className="mt-2 flex items-center gap-2">
              {trackingStatus === 'On Track' ? (
                <CheckCircle className="size-4 text-success" />
              ) : trackingStatus === 'At Risk' ? (
                <AlertTriangle className="size-4 text-warning" />
              ) : (
                <Clock className="size-4 text-destructive" />
              )}
              <span className="text-sm font-semibold text-foreground">{trackingStatus}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Milestones</CardTitle>
            <CardDescription>Upcoming deliverables and due dates</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <p className="text-sm text-muted-foreground">Loading milestones...</p>
            ) : milestoneItems.length === 0 ? (
              <p className="text-sm text-muted-foreground">No milestones scheduled.</p>
            ) : (
              <div className="space-y-2">
                {milestoneItems.map((item) => {
                  const dueDate = toDateValue(item.plannedEnd)
                  const overdue = dueDate ? dueDate.getTime() < Date.now() && item.status !== 'completed' : false
                  return (
                    <div key={item.id} className="flex items-center justify-between rounded-lg border p-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{item.name || 'Deliverable'}</p>
                        <p className="text-xs text-muted-foreground">
                          Due {formatDate(item.plannedEnd)}
                        </p>
                      </div>
                      <Badge variant="secondary" className={overdue ? 'text-destructive' : 'text-muted-foreground'}>
                        {overdue ? 'Overdue' : item.status || 'Scheduled'}
                      </Badge>
                    </div>
                  )
                })}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Risk Snapshot</CardTitle>
            <CardDescription>Open risks and severity</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <p className="text-sm text-muted-foreground">Loading risks...</p>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-lg border p-4">
                  <p className="text-xs text-muted-foreground">Open Risks</p>
                  <p className="text-2xl font-bold text-foreground">{openRisks}</p>
                </div>
                <div className="rounded-lg border p-4">
                  <p className="text-xs text-muted-foreground">High Risks</p>
                  <p className="text-2xl font-bold text-foreground">{highRisks}</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle className="text-base">Indicator Tracking</CardTitle>
            <CardDescription>Baseline, target, and current progress</CardDescription>
          </div>
          {canEdit ? (
            <Dialog open={indicatorOpen} onOpenChange={setIndicatorOpen}>
              <DialogTrigger asChild>
                <Button size="sm">
                  <Plus className="size-4 mr-1" />
                  Add Indicator
                </Button>
              </DialogTrigger>
              <DialogContent className="max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Add Indicator</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Indicator</Label>
                  <Input
                    value={indicatorForm.name}
                    onChange={(event) => setIndicatorForm((prev) => ({ ...prev, name: event.target.value }))}
                    placeholder="Indicator name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Level</Label>
                  <Select
                    value={indicatorForm.level}
                    onValueChange={(value) => setIndicatorForm((prev) => ({ ...prev, level: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select level" />
                    </SelectTrigger>
                    <SelectContent>
                      {['Goal', 'Outcome', 'Output', 'Activity'].map((level) => (
                        <SelectItem key={level} value={level}>
                          {level}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-3 sm:grid-cols-3">
                  <div className="space-y-2">
                    <Label>Baseline</Label>
                    <Input
                      type="number"
                      value={indicatorForm.baseline}
                      onChange={(event) => setIndicatorForm((prev) => ({ ...prev, baseline: event.target.value }))}
                      placeholder="0"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Target</Label>
                    <Input
                      type="number"
                      value={indicatorForm.target}
                      onChange={(event) => setIndicatorForm((prev) => ({ ...prev, target: event.target.value }))}
                      placeholder="0"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Current</Label>
                    <Input
                      type="number"
                      value={indicatorForm.current}
                      onChange={(event) => setIndicatorForm((prev) => ({ ...prev, current: event.target.value }))}
                      placeholder="0"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Data Source / Means of Verification</Label>
                  <Textarea
                    rows={3}
                    value={indicatorForm.dataSource}
                    onChange={(event) => setIndicatorForm((prev) => ({ ...prev, dataSource: event.target.value }))}
                    placeholder="Surveys, reports, monitoring data"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Attach Document (Optional)</Label>
                  <Input
                    type="file"
                    onChange={(event) => setIndicatorFile(event.target.files?.[0] || null)}
                  />
                  {indicatorFile && (
                    <p className="text-xs text-muted-foreground">
                      Selected: {indicatorFile.name}
                    </p>
                  )}
                </div>
                <Button className="w-full" onClick={handleSaveIndicator} disabled={savingIndicator}>
                  {savingIndicator ? 'Saving...' : 'Save Indicator'}
                </Button>
              </div>
              </DialogContent>
            </Dialog>
          ) : null}
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Indicator</TableHead>
                  <TableHead>Level</TableHead>
                  <TableHead>Baseline</TableHead>
                  <TableHead>Target</TableHead>
                  <TableHead>Current</TableHead>
                  <TableHead>Progress</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="py-6 text-center text-sm text-muted-foreground">
                      Loading indicators...
                    </TableCell>
                  </TableRow>
                ) : indicators.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="py-6 text-center text-sm text-muted-foreground">
                      No indicators defined yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  indicators.map((indicator) => {
                    const target = getIndicatorTarget(indicator)
                    const current = Number(indicator.actual ?? indicator.currentValue ?? 0)
                    const baseline = Number(indicator.baseline ?? 0)
                    const progress = target > 0 ? (current / target) * 100 : 0
                    return (
                      <TableRow key={indicator.id}>
                        <TableCell className="text-sm font-medium text-foreground">
                          {indicator.name || 'Indicator'}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {indicator.level || 'Output'}
                        </TableCell>
                        <TableCell>{baseline || '—'}</TableCell>
                        <TableCell>{target || '—'}</TableCell>
                        <TableCell>{current || '—'}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Progress value={progress} className="h-1.5 w-24" />
                            <span className="text-xs text-muted-foreground">{progress.toFixed(0)}%</span>
                          </div>
                        </TableCell>
                      </TableRow>
                    )
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
